/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react'
import { Box, IconButton, Paper, CircularProgress, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import type { DraggableAttributes } from '@dnd-kit/core'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import {
  ChartType,
  DataCombinationType,
  StatisticsDataType,
} from '~/interfaces/IStatistics'
import ColumnChartHorizontal from '~/components/ColumnChartMui/HorizontalChart'
import ColumnChartVertical from '~/components/ColumnChartMui/VerticalChart'
import PieChartMui from '~/components/PieChartMui'
import LineChartMui from '~/components/LineChartMui'
import NumericIndicator from '~/components/NumericIndicator'
import useSystemState from '~/features/System/stores/SystemState'

interface ChartWidgetProps {
  readonly id: string
  readonly chartType: ChartType
  readonly dataCombination: DataCombinationType
  readonly data: StatisticsDataType | null
  readonly loading: boolean
  readonly onRemove: (id: string) => void
  readonly chartIndex: number
  readonly dragHandleAttributes?: DraggableAttributes
  readonly dragHandleListeners?: SyntheticListenerMap
}

const getChartTitle = (dataCombination: DataCombinationType): string => {
  const titles: Record<DataCombinationType, string> = {
    [DataCombinationType.DISPATCH_BY_STATUS]: 'Total de Despachos por Status',
    [DataCombinationType.DEVICE_BY_STATUS]: 'Total de Dispositivos por Status',
    [DataCombinationType.DISPATCH_BY_TYPE]: 'Total de Despachos por Tipo',
    [DataCombinationType.DISPATCH_BY_SUBTYPE]: 'Total de Despachos por Subtipo',
    [DataCombinationType.DISPATCH_BY_CLOSED_STATUS]:
      'Total de Despachos por Status de Fechamento',
    [DataCombinationType.DISPATCH_BY_DISPATCH_GROUP]:
      'Total de Despachos por Grupo de Despacho',
    [DataCombinationType.DISPATCH_BY_CITY]: 'Total de Despachos por Cidade',
    [DataCombinationType.DISPATCH_BY_DISTRICT]: 'Total de Despachos por Bairro',
    [DataCombinationType.DISPATCH_BY_DEVICE]:
      'Total de Despachos por Dispositivo',
    [DataCombinationType.DISPATCHES_BY_HOURS]:
      'Total de Despachos por Intervalo de Hora',
    [DataCombinationType.DISPATCHES_BY_WEEKDAY]:
      'Total de Despachos por Dia da Semana',
  }
  return titles[dataCombination]
}

const getTypeLabel = (dataCombination: DataCombinationType): string => {
  const typeLabels: Record<DataCombinationType, string> = {
    [DataCombinationType.DISPATCH_BY_STATUS]: 'Status',
    [DataCombinationType.DEVICE_BY_STATUS]: 'Status',
    [DataCombinationType.DISPATCH_BY_TYPE]: 'Tipos',
    [DataCombinationType.DISPATCH_BY_SUBTYPE]: 'Subtipos',
    [DataCombinationType.DISPATCH_BY_CLOSED_STATUS]: 'Status de Fechamento',
    [DataCombinationType.DISPATCH_BY_DISPATCH_GROUP]: 'Grupos de Despacho',
    [DataCombinationType.DISPATCH_BY_CITY]: 'Cidades',
    [DataCombinationType.DISPATCH_BY_DISTRICT]: 'Bairros',
    [DataCombinationType.DISPATCH_BY_DEVICE]: 'Dispositivos',
    [DataCombinationType.DISPATCHES_BY_HOURS]: 'Intervalos de Hora',
    [DataCombinationType.DISPATCHES_BY_WEEKDAY]: 'Dias da Semana',
  }
  return typeLabels[dataCombination]
}

const getItemLabel = (dataCombination: DataCombinationType): string => {
  if (dataCombination === DataCombinationType.DEVICE_BY_STATUS) {
    return 'Dispositivos'
  }
  return 'Despachos'
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const transformDataForChart = (
  data: StatisticsDataType,
  dataCombination: DataCombinationType,
  getStatusColors: any,
  getDeviceStatusColors: any,
  withColors = false
): readonly {
  readonly item: string
  readonly value: number
  readonly id?: number
  readonly label: string
  readonly total: number
  readonly color?: string
}[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return []
  }

  // Para dados com range (horas)
  if ('range' in data[0]) {
    return data.map(
      (item: { readonly range: string; readonly total: number }) => ({
        item: item.range,
        value: item.total,
        label: item.range,
        total: item.total,
      })
    )
  }

  // Para dados com weekday
  if ('weekday' in data[0]) {
    const weekdayTranslation: Record<string, string> = {
      MONDAY: 'Segunda-feira',
      TUESDAY: 'Terça-feira',
      WEDNESDAY: 'Quarta-feira',
      THURSDAY: 'Quinta-feira',
      FRIDAY: 'Sexta-feira',
      SATURDAY: 'Sábado',
      SUNDAY: 'Domingo',
    }
    return data.map(
      (item: { readonly weekday: string; readonly total: number }) => ({
        item: weekdayTranslation[item.weekday] || item.weekday,
        value: item.total,
        label: weekdayTranslation[item.weekday] || item.weekday,
        total: item.total,
      })
    )
  }

  // Para dados padrão com name e total
  return data.map(
    (item: {
      readonly name: string
      readonly total: number
      readonly id?: number
    }) => {
      const baseData = {
        item: item.name,
        value: item.total,
        label: item.name,
        total: item.total,
        ...(item.id && { id: item.id }),
      }

      // Adiciona cor apenas quando solicitado (gráficos de pizza e indicador numérico)
      if (
        withColors &&
        dataCombination === DataCombinationType.DISPATCH_BY_STATUS &&
        item.id
      ) {
        const colorObj = getStatusColors(item.id)
        const color = typeof colorObj === 'string' ? colorObj : colorObj?.main
        return { ...baseData, color }
      }

      if (
        withColors &&
        dataCombination === DataCombinationType.DEVICE_BY_STATUS &&
        item.id
      ) {
        const colorObj = getDeviceStatusColors(item.id)
        const color = typeof colorObj === 'string' ? colorObj : colorObj?.main
        return { ...baseData, color }
      }

      return baseData
    }
  )
}

export default function ChartWidget({
  id,
  chartType,
  dataCombination,
  data,
  loading,
  onRemove,
  chartIndex,
  dragHandleAttributes,
  dragHandleListeners,
}: ChartWidgetProps) {
  const title = getChartTitle(dataCombination)
  const { getStatusColors, getDeviceStatusColors } = useSystemState()

  // Define a cor do gráfico baseado no índice (rotaciona entre primary, secondary, tertiary)
  const colorKeys = ['primary', 'secondary', 'tertiary'] as const
  const colorBar = colorKeys[chartIndex % colorKeys.length]

  const renderChart = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={300}
        >
          <CircularProgress />
        </Box>
      )
    }

    if (!data || (Array.isArray(data) && data.length === 0)) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={300}
        >
          Sem dados disponíveis
        </Box>
      )
    }

    const withColors =
      chartType === ChartType.PIE || chartType === ChartType.NUMERIC

    const chartData = transformDataForChart(
      data,
      dataCombination,
      getStatusColors,
      getDeviceStatusColors,
      withColors
    )

    // Calcula total de itens e tipos
    const totalItems = chartData.reduce((sum, item) => sum + item.value, 0)
    const totalTypes = chartData.length
    const itemLabel = getItemLabel(dataCombination)
    const typeLabel = getTypeLabel(dataCombination)
    // eslint-disable-next-line new-cap
    const subtitle = `Total de ${itemLabel}: ${Intl.NumberFormat('pt-BR').format(totalItems)} | Total de ${typeLabel}: ${totalTypes}`

    switch (chartType) {
      case ChartType.NUMERIC:
        return (
          <NumericIndicator
            data={chartData.map((item) => ({ name: item.item, total: item.total, color: item.color }))}
            title={title}
          />
        )
      case ChartType.BAR:
        return <ColumnChartHorizontal data={chartData} title={title} subtitle={subtitle} colorBar={colorBar} fullHeight />
      case ChartType.COLUMN:
        return <ColumnChartVertical data={chartData} title={title} subtitle={subtitle} colorBar={colorBar} fullHeight />
      case ChartType.PIE:
        return <PieChartMui data={chartData} title={title} subtitle={subtitle} legendPosition="bottom" showPercent={true} compactLegend={true} />
      case ChartType.LINE:
        return <LineChartMui data={chartData} title={title} subtitle={subtitle} shouldEnableScroll />
      default:
        return <div>Tipo de gráfico não suportado</div>
    }
  }

  return (
    <Paper
      sx={{
        position: 'relative',
        height: chartType === ChartType.NUMERIC ? 'fit-content' : '100%',
        minHeight: chartType === ChartType.NUMERIC ? 'unset' : 350,
        '&:hover .drag-handle-bar': {
          opacity: 1,
          backgroundColor: 'action.hover',
        },
      }}
    >
      {/* Barra superior de drag */}
      <Tooltip title="Arrastar para reorganizar" placement="top">
        <Box
          component="div"
          role="button"
          tabIndex={0}
          aria-label="Arrastar gráfico"
          className="drag-handle-bar"
          {...dragHandleAttributes}
          {...dragHandleListeners}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            cursor: 'grab',
            opacity: 0.45,
            transition: 'opacity 0.2s, background-color 0.2s',
            borderRadius: '4px 4px 0 0',
            zIndex: 1,
            '&:active': {
              cursor: 'grabbing',
              opacity: 1,
            },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 22, color: 'text.secondary', rotate: '90deg' }} />
        </Box>
      </Tooltip>
      <IconButton
        onClick={() => onRemove(id)}
        sx={{
          position: 'absolute',
          top: 4,
          right: 8,
          zIndex: 2,
        }}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {renderChart()}
    </Paper>
  )
}
